import { useEffect, useState } from 'react'
import { Download, Play, Trash2 } from 'lucide-react'
import type { Job } from '@shared/types'
import { useEditorStore } from '../../../store/editorStore'
import { useUiStore } from '../../../store/uiStore'
import { EmptyState } from '../../common/States'
import { ConfirmDialog } from '../../common/ConfirmDialog'
import { api } from '../../../api'

/**
 * P10-3 担当A (docs/P10_PHASE3.md §2 item 5): the export tab's「過去の書き出し」
 * list. Originally cut out of TabExport.tsx verbatim by the 司令塔 (behaviour
 * unchanged at that point); rewritten here for P10-3's 3 requirements:
 *   - `renderHistory` is now the raw `Job[]` (editorStore.ts §2 item 6) —
 *     size/尺/容量 come from `Job.result` (absent on a pre-P10-3 "legacy" Job
 *     -> shown as "—", never crash), status 'error' shows ✕ + the message.
 *   - ダウンロード（GET /api/jobs/:id/download, forces a save with a good
 *     filename）separate from 再生（unchanged `api.fileUrl` in a new tab）.
 *   - 削除（DELETE /api/jobs/:id via common/ConfirmDialog — 409 keeps the
 *     dialog open with the conflict message, same convention every other
 *     delete flow in this app already uses）.
 *   - スコープ切替「この構成案／プロジェクト全体」(uiStore's exportHistoryScope,
 *     not persisted). Composition scope reads `renderHistory` straight from
 *     the store (kept fresh there by openProject's hydrate + requestRender's
 *     onDone refetch); project scope fetches its own list here via
 *     `api.listProjectJobs`, refetched whenever the scope is 'project' and
 *     whenever the store's `renderHistory` changes (a new render just
 *     completed) or a row here is deleted.
 */

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// reviewer minor A-4: decimal MB (bytes / 1e6), matching how every OS file
// picker/Finder/Explorer labels file size — NOT MiB (/1024/1024), which
// under-reports against what the user sees when they actually go find the
// downloaded file (e.g. a real 8.4MB-on-disk file showing "8.0 MB" here).
function formatBytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

function formatDurationSec(sec: number): string {
  return `${sec.toFixed(1)}s`
}

export function ExportHistory() {
  const composition = useEditorStore((s) => s.composition)
  const currentProject = useEditorStore((s) => s.currentProject)
  const compositionScopedJobs = useEditorStore((s) => s.renderHistory)
  const scope = useUiStore((s) => s.exportHistoryScope)
  const setScope = useUiStore((s) => s.setExportHistoryScope)

  const [projectJobs, setProjectJobs] = useState<Job[]>([])
  const [projectJobsLoading, setProjectJobsLoading] = useState(false)
  // Composition name for a project-scope row whose Job has no `.result`
  // (legacy) AND isn't the currently-open Composition — resolved lazily
  // below. `null` = that Composition has since been deleted.
  const [compositionNames, setCompositionNames] = useState<Record<string, string | null>>({})
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  useEffect(() => {
    if (scope !== 'project' || !currentProject) return
    let cancelled = false
    setProjectJobsLoading(true)
    api
      .listProjectJobs(currentProject.id, 'render')
      .then((jobs) => {
        if (!cancelled) setProjectJobs(jobs)
      })
      .catch(() => {
        if (!cancelled) setProjectJobs([])
      })
      .finally(() => {
        if (!cancelled) setProjectJobsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // `compositionScopedJobs` isn't read in the body — its IDENTITY changing
    // is the signal a render just finished (requestRender's onDone refetch)
    // or a new project/composition was opened (openProject's hydrate), so
    // the project-wide list is refetched too instead of going stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, currentProject, compositionScopedJobs])

  const jobs = scope === 'project' ? projectJobs : compositionScopedJobs

  useEffect(() => {
    if (scope !== 'project') return
    const idsNeeded = Array.from(
      new Set(
        jobs
          .filter((j) => !j.result && j.compositionId && j.compositionId !== composition?.id)
          .map((j) => j.compositionId as string),
      ),
    ).filter((id) => !(id in compositionNames))
    if (idsNeeded.length === 0) return
    let cancelled = false
    Promise.all(
      idsNeeded.map(async (id): Promise<[string, string | null]> => {
        try {
          const c = await api.getComposition(id)
          return [id, c.name]
        } catch {
          return [id, null]
        }
      }),
    ).then((pairs) => {
      if (cancelled) return
      setCompositionNames((prev) => {
        const next = { ...prev }
        for (const [id, name] of pairs) next[id] = name
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [scope, jobs, composition?.id, compositionNames])

  function compositionNameFor(job: Job): string {
    if (job.result) return job.result.compositionName
    if (job.compositionId === composition?.id) return composition?.name ?? '（削除済み）'
    if (job.compositionId && job.compositionId in compositionNames) {
      return compositionNames[job.compositionId] ?? '（削除済み）'
    }
    return '…'
  }

  async function handleDelete(jobId: string) {
    await api.deleteJob(jobId)
    setProjectJobs((prev) => prev.filter((j) => j.id !== jobId))
    // The store owns composition-scope's list — drop the row there too so
    // both scopes agree regardless of which is currently showing.
    useEditorStore.setState((s) => ({ renderHistory: s.renderHistory.filter((j) => j.id !== jobId) }))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-medium text-text-muted">過去の書き出し</div>
        <div
          className="flex rounded border border-ink-border overflow-hidden text-[10px]"
          data-testid="export-history-scope"
        >
          <button
            type="button"
            onClick={() => setScope('composition')}
            className={`px-2 py-1 ${scope === 'composition' ? 'bg-accent-blue/20 text-accent-blue' : 'text-text-faint'}`}
            data-testid="export-history-scope-composition"
          >
            この構成案
          </button>
          <button
            type="button"
            onClick={() => setScope('project')}
            className={`px-2 py-1 ${scope === 'project' ? 'bg-accent-blue/20 text-accent-blue' : 'text-text-faint'}`}
            data-testid="export-history-scope-project"
          >
            プロジェクト全体
          </button>
        </div>
      </div>

      {jobs.length === 0 ? (
        <EmptyState title={scope === 'project' && projectJobsLoading ? '読み込み中…' : 'まだ書き出していません'} />
      ) : (
        <ul className="space-y-1.5" data-testid="export-history-list">
          {jobs.map((job) => (
            <li
              key={job.id}
              className="rounded px-2 py-1.5 bg-ink-surface2/40 text-xs space-y-1"
              data-testid="export-history-row"
            >
              <div className="flex items-center justify-between gap-2">
                {/* reviewer minor A-5: createdAt — the SAME basis buildDownloadFilename
                    uses for the file's own timestamp — not updatedAt (which keeps
                    ticking while queued/running, disagreeing with the filename). */}
                <span>{formatDateTime(job.createdAt)}</span>
                {scope === 'project' && (
                  <span className="text-text-faint truncate max-w-[130px]" data-testid="export-history-composition-name">
                    {compositionNameFor(job)}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 text-text-faint" data-testid="export-history-meta">
                {job.status === 'queued' || job.status === 'running' ? (
                  <span data-testid="export-history-processing">処理中… {Math.round(job.progress * 100)}%</span>
                ) : (
                  <span>
                    {job.result ? `${job.result.output.width}×${job.result.output.height}` : '—'}
                    {' ・ '}
                    {job.result ? formatDurationSec(job.result.durationSec) : '—'}
                    {' ・ '}
                    {job.result ? formatBytes(job.result.bytes) : '—'}
                  </span>
                )}
                {job.status === 'error' && (
                  <span className="text-accent-red truncate max-w-[140px]" data-testid="export-history-error">
                    ✕ {job.error ?? '不明なエラー'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 pt-0.5">
                {job.status === 'done' && job.resultPath && (
                  <>
                    <a
                      href={api.jobDownloadUrl(job.id)}
                      download
                      className="flex items-center gap-1 text-accent-blue hover:underline"
                      data-testid="export-history-download"
                    >
                      <Download size={12} />
                      ダウンロード
                    </a>
                    <a
                      href={api.fileUrl(job.resultPath)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-accent-blue hover:underline"
                      data-testid="export-history-play"
                    >
                      <Play size={12} />
                      再生
                    </a>
                  </>
                )}
                {/* reviewer minor A-3: queued/running rows can't be deleted (server
                    already 409s `job_running`) — disable instead of letting the
                    click round-trip into a conflict dialog every time. */}
                <button
                  type="button"
                  onClick={() => setPendingDeleteId(job.id)}
                  disabled={job.status === 'queued' || job.status === 'running'}
                  title={
                    job.status === 'queued' || job.status === 'running'
                      ? '処理中のため削除できません'
                      : undefined
                  }
                  className="flex items-center gap-1 text-text-faint hover:text-accent-red ml-auto disabled:opacity-40 disabled:hover:text-text-faint disabled:cursor-not-allowed"
                  data-testid="export-history-delete"
                >
                  <Trash2 size={12} />
                  削除
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pendingDeleteId && (
        <ConfirmDialog
          title="書き出しを削除しますか"
          message="このファイルは復元できません。"
          onCancel={() => setPendingDeleteId(null)}
          onConfirm={async () => {
            await handleDelete(pendingDeleteId)
            setPendingDeleteId(null)
          }}
          busyMessage="処理中のため削除できません。しばらく待ってから再度お試しください。"
        />
      )}
    </div>
  )
}
